/**
 * AliasesEditor — chip editor for a category's synonyms/aliases (PIM Wave P1
 * Task 6, design notes Surface 2 / brief §2). Mounts inside
 * `CategoryFormDialog` in EDIT MODE ONLY — aliases nest under a category id,
 * so there is nothing to attach them to before the category itself exists
 * (same reasoning as `OptionsEditor` needing a saved field id).
 *
 * Mutations are IMMEDIATE and independent of the dialog's own save (the
 * `OptionsEditor` precedent, `attribute-fields/components/OptionsEditor.tsx`):
 * add = POST (409 duplicate, `marketplace.category_alias.duplicate` —
 * inline error under the input, input keeps its value so the admin can
 * fix/retry without retyping), remove = DELETE (no confirm — low stakes,
 * additive back, brief §2). There is no alias PATCH here: the chip editor
 * is add/remove only (brief §1), `CategoryAliasUpdate` is unused.
 *
 * Chips are a bespoke pill (not `GPill`) for the same reason
 * `TrilingualInput`'s parity chips are bespoke: `GPill` has no trailing-slot
 * passthrough for a remove button. Removable via keyboard (a focusable
 * `<button>` with an `aria-label`, brief §5).
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X } from "lucide-react";

import { Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import type { CategoryAlias } from "@/features/admin/categories/api";
import {
  useCategoryAliases,
  useCreateCategoryAlias,
  useDeleteCategoryAlias,
} from "@/features/admin/categories/hooks";

const SELECT_CLASS =
  "min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

const INPUT_CLASS =
  "min-h-[44px] flex-1 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

type AliasLangOption = "" | "ru" | "ky" | "en";

interface AliasChipProps {
  categoryId: string;
  alias: CategoryAlias;
}

function AliasChip({ categoryId, alias }: AliasChipProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const deleteMutation = useDeleteCategoryAlias();

  const remove = (): void => {
    deleteMutation.mutate(
      { categoryId, aliasId: alias.id },
      { onError: () => toast.error(t("error.generic")) },
    );
  };

  return (
    <span
      data-testid="category-alias-chip"
      className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-[var(--g-hairline)] bg-[var(--g-surface)] py-1 pl-3 pr-1 text-sm"
    >
      <span className="truncate">{alias.alias}</span>
      {alias.lang ? (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
          {alias.lang}
        </span>
      ) : null}
      <button
        type="button"
        data-testid={`category-alias-remove-${alias.id}`}
        aria-label={t("categories.aliases.remove", { alias: alias.alias })}
        disabled={deleteMutation.isPending}
        onClick={remove}
        className="grid size-6 shrink-0 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <X size={14} strokeWidth={1.75} aria-hidden />
      </button>
    </span>
  );
}

export interface AliasesEditorProps {
  categoryId: string;
}

export function AliasesEditor({ categoryId }: AliasesEditorProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isLoading, isError, refetch } = useCategoryAliases(categoryId);
  const aliases = data?.aliases ?? [];

  const [draftAlias, setDraftAlias] = useState("");
  const [draftLang, setDraftLang] = useState<AliasLangOption>("");
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const createMutation = useCreateCategoryAlias();

  const trimmed = draftAlias.trim();
  const canSubmit = trimmed !== "" && !createMutation.isPending;

  const submit = (event?: FormEvent): void => {
    event?.preventDefault();
    if (!canSubmit) return;
    setDuplicateError(null);
    createMutation.mutate(
      { categoryId, body: { alias: trimmed, lang: draftLang === "" ? null : draftLang } },
      {
        onSuccess: () => {
          setDraftAlias("");
          setDraftLang("");
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "marketplace.category_alias.duplicate") {
            setDuplicateError(t("categories.aliases.add.duplicateError"));
            return;
          }
          toast.error(t("error.generic"));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid="category-aliases-editor">
      <span className="text-[13px] font-semibold text-foreground">{t("categories.aliases.title")}</span>

      {isLoading ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("categories.aliases.loading")}</p>
      ) : isError ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-[var(--g-error)]">{t("categories.aliases.loadError")}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()} className="min-h-[44px]">
            {t("categories.aliases.retry")}
          </Button>
        </div>
      ) : aliases.length === 0 ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("categories.aliases.empty")}</p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="category-aliases-list">
          {aliases.map((alias) => (
            <AliasChip key={alias.id} categoryId={categoryId} alias={alias} />
          ))}
        </div>
      )}

      <form
        onSubmit={submit}
        className="flex flex-col gap-2 rounded-[var(--g-radius-sm)] border border-dashed border-[var(--g-hairline)] p-3 sm:flex-row sm:items-start"
        data-testid="category-alias-add-row"
      >
        <div className="flex-1">
          <label className="sr-only" htmlFor="category-alias-add-input">
            {t("categories.aliases.add.label")}
          </label>
          <input
            id="category-alias-add-input"
            data-testid="category-alias-add-input"
            className={INPUT_CLASS}
            placeholder={t("categories.aliases.add.label")}
            value={draftAlias}
            onChange={(event) => {
              setDuplicateError(null);
              setDraftAlias(event.target.value);
            }}
          />
          {duplicateError ? (
            <p role="alert" data-testid="category-alias-add-error" className="mt-1 text-xs text-[var(--g-error)]">
              {duplicateError}
            </p>
          ) : null}
        </div>
        <label className="sr-only" htmlFor="category-alias-add-lang">
          {t("categories.aliases.add.langLabel")}
        </label>
        <select
          id="category-alias-add-lang"
          data-testid="category-alias-add-lang"
          className={SELECT_CLASS}
          value={draftLang}
          onChange={(event) => setDraftLang(event.target.value as AliasLangOption)}
        >
          <option value="">{t("categories.aliases.add.langNone")}</option>
          <option value="ru">RU</option>
          <option value="ky">KG</option>
          <option value="en">EN</option>
        </select>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          leading="plus"
          loading={createMutation.isPending}
          disabled={!canSubmit}
          data-testid="category-alias-add-submit"
          className="min-h-[44px]"
        >
          {t("categories.aliases.add.submit")}
        </Button>
      </form>
    </div>
  );
}

export default AliasesEditor;
